import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { ActivityTypeId } from '@/lib/api/company/chartScan/types'
import {
  faArrowRotateLeftSolid,
  faArrowRotateRightSolid,
  faArrowsRotateSolid,
  faCompressSolid,
  faExpandSolid,
  faMagnifyingGlassMinusSolid,
  faMagnifyingGlassPlusSolid,
  faRulerHorizontalSolid,
} from '@/lib/fa'
import { useChartT } from '../i18n'
import { ChartActivityModeButtons } from './ChartActivityModeButtons'

type Props = {
  addingMode: ActivityTypeId | null
  onChangeMode: (mode: ActivityTypeId | null) => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  zoom: number
  onZoomIn: () => void
  onZoomOut: () => void
  onRefresh?: () => void
  showDistance: boolean
  onToggleDistance: () => void
  compactView: boolean
  onToggleCompactView: () => void
}

export function ChartImportToolbar({
  addingMode,
  onChangeMode,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  zoom,
  onZoomIn,
  onZoomOut,
  onRefresh,
  showDistance,
  onToggleDistance,
  compactView,
  onToggleCompactView,
}: Props) {
  const T = useChartT()
  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5">
      <ChartActivityModeButtons
        addingMode={addingMode}
        onChangeMode={onChangeMode}
      />
      <div className="tw-flex tw-items-center tw-gap-1">
        <ToolbarBtn
          title={
            compactView
              ? T('expandChart', 'Powiększ widok tarczki (jak w WPF)')
              : T('shrinkChart', 'Zmniejsz widok tarczki (wypełnij obszar)')
          }
          onClick={onToggleCompactView}
          active={compactView}
          icon={(compactView ? faExpandSolid : faCompressSolid) as IconProp}
        />
        <ToolbarBtn
          title={
            showDistance
              ? T('hideDistancePoints', 'Ukryj punkty dystansu')
              : T('showDistancePoints', 'Pokaż punkty dystansu')
          }
          onClick={onToggleDistance}
          active={showDistance}
          icon={faRulerHorizontalSolid as IconProp}
        />
        {onRefresh && (
          <ToolbarBtn
            title={T('reloadActivities', 'Wczytaj ponownie aktywności')}
            onClick={onRefresh}
            icon={faArrowsRotateSolid as IconProp}
          />
        )}
        <ToolbarBtn
          title={T('undo', 'Cofnij (Ctrl+Z)')}
          disabled={!canUndo}
          onClick={onUndo}
          icon={faArrowRotateLeftSolid as IconProp}
        />
        <ToolbarBtn
          title={T('redo', 'Ponów (Ctrl+Y)')}
          disabled={!canRedo}
          onClick={onRedo}
          icon={faArrowRotateRightSolid as IconProp}
        />
        <span className="tw-px-2 tw-font-mono tw-text-xs tw-text-slate-500">
          {Math.round(zoom * 100)}%
        </span>
        <ToolbarBtn
          title={T('zoomOut', 'Pomniejsz')}
          onClick={onZoomOut}
          icon={faMagnifyingGlassMinusSolid as IconProp}
        />
        <ToolbarBtn
          title={T('zoomIn', 'Powiększ')}
          onClick={onZoomIn}
          icon={faMagnifyingGlassPlusSolid as IconProp}
        />
      </div>
    </div>
  )
}

function ToolbarBtn({
  icon,
  title,
  onClick,
  disabled,
  active,
}: {
  icon: IconProp
  title: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={[
        'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded tw-border tw-text-sm',
        disabled
          ? 'tw-cursor-not-allowed tw-border-slate-300 tw-bg-white tw-opacity-40'
          : active
            ? 'tw-border-blue-600 tw-bg-blue-50 tw-text-blue-700'
            : 'tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-border-slate-500 hover:tw-bg-slate-50',
      ].join(' ')}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  )
}
